import { FinancialAnalysisGranularity } from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchCostAnalysisQueryOptions,
	fetchExpenseCategoriesQueryOptions,
} from "@/api/orgMgmtCostManagement";
import { formatCurrency } from "@/components/billing/CurrencyDisplay";
import {
	computePresetRange,
	type DateRange,
	dateInputValueToIso,
	RangeFilters,
	type RangePreset,
} from "@/components/financial/RangeFilters";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useCategoryNameResolver } from "@/lib/expense-category-name";
import { OrganizationManagementNavigation } from "@/routes/_auth/app/_app/org/$orgId/org-management/-components/OrganizationManagementNavigation";
import { CategoryBreakdownDonut } from "./-components/CategoryBreakdownDonut";
import { CostAnalysisChart } from "./-components/CostAnalysisChart";
import { CostManagementSubNav } from "./-components/CostManagementSubNav";

const DEFAULT_GRANULARITY = FinancialAnalysisGranularity.MONTH;
const DEFAULT_PRESET: RangePreset = "6m";

const DEFAULT_RANGE = computePresetRange(DEFAULT_PRESET);

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/org-management/cost-management/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;
		context.queryClient.ensureQueryData(
			fetchCostAnalysisQueryOptions(
				orgId,
				token,
				DEFAULT_GRANULARITY,
				DEFAULT_RANGE,
			),
		);
		context.queryClient.ensureQueryData(
			fetchExpenseCategoriesQueryOptions(orgId, token),
		);
		return { userId, token, orgId };
	},
	component: CostManagementAnalysisPage,
});

function CostManagementAnalysisPage() {
	const { t } = useTranslation();
	const { token, orgId } = Route.useLoaderData();

	const [granularity, setGranularity] =
		useState<FinancialAnalysisGranularity>(DEFAULT_GRANULARITY);
	const [preset, setPreset] = useState<RangePreset>(DEFAULT_PRESET);
	const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
	const [validationError, setValidationError] = useState<string | null>(null);

	const { data, isLoading, isError } = useQuery(
		fetchCostAnalysisQueryOptions(orgId, token, granularity, range),
	);
	const resolveCategoryName = useCategoryNameResolver(orgId, token);

	const currency = data?.currency ?? "BRL";
	const currentPeriodTotal = data?.buckets.at(-1)?.totalAmount ?? "0";

	const pendingCount = useMemo(() => {
		if (!data) return 0;
		return data.buckets.reduce((acc, b) => acc + (b.expenseCount ?? 0), 0);
	}, [data]);

	const translatedCategoryTotals = useMemo(() => {
		if (!data) return [];
		return data.byCategoryTotals.map((c) => ({
			...c,
			categoryName: resolveCategoryName({
				id: c.categoryId,
				name: c.categoryName,
				color: c.color,
			}),
		}));
	}, [data, resolveCategoryName]);

	const topCategory = useMemo(() => {
		if (translatedCategoryTotals.length === 0) return null;
		return translatedCategoryTotals[0];
	}, [translatedCategoryTotals]);

	const handlePresetChange = (next: RangePreset) => {
		setPreset(next);
		setValidationError(null);
		if (next !== "custom") {
			setRange(computePresetRange(next));
		}
	};

	const applyCustomRange = (nextRange: DateRange) => {
		if (
			new Date(nextRange.toDate).getTime() <=
			new Date(nextRange.fromDate).getTime()
		) {
			setValidationError(
				t("orgManagement.financialAnalysis.filters.invalidRange"),
			);
			return;
		}
		setValidationError(null);
		setPreset("custom");
		setRange(nextRange);
	};

	const handleCustomFromChange = (value: string) => {
		if (!value) return;
		const nextFrom = dateInputValueToIso(value, false);
		applyCustomRange({ fromDate: nextFrom, toDate: range.toDate });
	};

	const handleCustomToChange = (value: string) => {
		if (!value) return;
		const nextTo = dateInputValueToIso(value, true);
		applyCustomRange({ fromDate: range.fromDate, toDate: nextTo });
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgManagement.costManagement.title")}
				description={t(
					"orgManagement.costManagement.subtitleVariants.analysis",
				)}
				showBackButton={true}
				backButtonText={t("orgManagement.back")}
				hideOnMobile={false}
			/>
			<div className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden">
				<OrganizationManagementNavigation activeTab="cost-management" />
				<div className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto">
					<div className="space-y-4 max-w-6xl mx-auto">
						<CostManagementSubNav orgId={orgId} active="analysis" />

						<RangeFilters
							preset={preset}
							range={range}
							validationError={validationError}
							onPresetChange={handlePresetChange}
							onFromChange={handleCustomFromChange}
							onToChange={handleCustomToChange}
							idPrefix="cost-management"
							leftSlot={
								<GranularityToggle
									value={granularity}
									onChange={setGranularity}
								/>
							}
						/>

						<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
							<KpiCard
								label={t("orgManagement.costManagement.kpis.currentPeriod")}
								value={formatCurrency(currentPeriodTotal, currency)}
							/>
							<KpiCard
								label={t("orgManagement.costManagement.kpis.totalWindow")}
								value={formatCurrency(data?.totalsAllPeriods ?? "0", currency)}
							/>
							<KpiCard
								label={t("orgManagement.costManagement.kpis.topCategory")}
								value={topCategory ? topCategory.categoryName : "—"}
								valueClassName="line-clamp-2"
							/>
							<KpiCard
								label={t("orgManagement.costManagement.kpis.expensesCount")}
								value={String(pendingCount)}
							/>
						</div>

						{isLoading && (
							<Card>
								<CardContent className="p-3 md:p-4">
									<div className="flex items-center justify-center h-64 text-base-content/60 text-sm">
										{t("orgManagement.costManagement.loading")}
									</div>
								</CardContent>
							</Card>
						)}
						{isError && (
							<Card>
								<CardContent className="p-3 md:p-4">
									<div className="flex items-center justify-center h-64 text-error text-sm">
										{t("orgManagement.costManagement.error")}
									</div>
								</CardContent>
							</Card>
						)}
						{!isLoading && !isError && data && (
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
								<Card>
									<CardContent className="p-3 md:p-4 space-y-2">
										<h3 className="text-sm font-semibold">
											{t("orgManagement.costManagement.chart.stackedTitle")}
										</h3>
										<CostAnalysisChart
											buckets={data.buckets}
											granularity={granularity}
											currency={currency}
										/>
									</CardContent>
								</Card>
								<Card>
									<CardContent className="p-3 md:p-4 space-y-2">
										<h3 className="text-sm font-semibold">
											{t("orgManagement.costManagement.chart.donutTitle")}
										</h3>
										<CategoryBreakdownDonut
											data={translatedCategoryTotals}
											currency={currency}
										/>
									</CardContent>
								</Card>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function GranularityToggle({
	value,
	onChange,
}: {
	value: FinancialAnalysisGranularity;
	onChange: (next: FinancialAnalysisGranularity) => void;
}) {
	const { t } = useTranslation();
	return (
		<div className="flex gap-1 md:gap-0 md:join" role="tablist">
			<button
				type="button"
				role="tab"
				aria-selected={value === FinancialAnalysisGranularity.WEEK}
				className={`btn btn-sm md:join-item ${
					value === FinancialAnalysisGranularity.WEEK
						? "btn-primary"
						: "btn-ghost"
				}`}
				onClick={() => onChange(FinancialAnalysisGranularity.WEEK)}
			>
				{t("orgManagement.financialAnalysis.granularity.week")}
			</button>
			<button
				type="button"
				role="tab"
				aria-selected={value === FinancialAnalysisGranularity.MONTH}
				className={`btn btn-sm md:join-item ${
					value === FinancialAnalysisGranularity.MONTH
						? "btn-primary"
						: "btn-ghost"
				}`}
				onClick={() => onChange(FinancialAnalysisGranularity.MONTH)}
			>
				{t("orgManagement.financialAnalysis.granularity.month")}
			</button>
		</div>
	);
}

interface KpiCardProps {
	label: string;
	value: string;
	valueClassName?: string;
}

function KpiCard({ label, value, valueClassName }: KpiCardProps) {
	return (
		<Card>
			<CardContent className="p-3 md:p-4">
				<p className="text-xs md:text-sm text-base-content/60">{label}</p>
				<p
					className={`mt-1 text-lg md:text-xl font-semibold text-base-content tabular-nums ${valueClassName ?? "truncate"}`}
				>
					{value}
				</p>
			</CardContent>
		</Card>
	);
}
